import React, { useState } from 'react';
import BinImage from '../../../images/Bin.svg';
import FormField from '../../formField/FormField';
import "./quesAnswerForm.scss";
import PropTypes from 'prop-types';
import { useAlert } from 'react-alert';
import { useTranslation } from 'react-i18next'; 
import OutlinedPinkButton from '../../buttons/outlinedPinkButton/OutlinedPinkButton';
import GreenRedOutlinedButtons from '../../buttons/greenRedOutlinedButtons/GreenRedOutlinedButtons';
import useAnsQuesValidationSchema from '../../validationsSchemas/useAnsQuesValidationSchema';

function QuesAnswerForm({ questions, setQuestion, generalError }) {
    const [questionName, setQuestionName] = useState('');
    const [questionAnswers, setQuestionAnswers] = useState([{ index: 1, text: '' }, { index: 2, text: ''}]);

    const [questionNameError, setQuestionNameError] = useState(null);
    const [questionAnswersError, setQuestionAnswersError] = useState(null);

    const [showForm, setShowForm] = useState(true);
    const alert = useAlert();

    const ansQuesValidationSchema = useAnsQuesValidationSchema(); 
    const { t } = useTranslation('quesAnsForm');

    async function handleNewQuestion() {
        setQuestionAnswersError(null);
        setQuestionNameError(null);

        const validAnswers = questionAnswers
            .filter(answer => answer.text.trim() !== '')
            .map((answer, index) => ({ ...answer, index: index + 1 }));

        try {
            const formattedQuestion = {question: questionName, answers: validAnswers};
            await ansQuesValidationSchema.validate(formattedQuestion, { abortEarly: false });
            setQuestion([...questions, formattedQuestion]);
            setQuestionName('');
            setQuestionAnswers([{ index: 1, text: '' }, { index: 2, text: ''}]);
        } 
        catch (err) {
            err.inner.reduce((acc, error) => {
                if (error.path === 'question') {
                    setQuestionNameError(error.message);
                }
                if (error.path === 'answers') {
                    setQuestionAnswersError(error.message);
                }
                return acc;
            }, {});

            alert.show(t('someFieldsAreIncorrect'));
        }
    }

    function handleAddAnswer() {
        const newIndex = questionAnswers.length + 1;
        setQuestionAnswersError(null);
        setQuestionAnswers([...questionAnswers, { index: newIndex, text: '' }]);
    }

    function handleAnswerChange(index, value) {
        const newAnswers = questionAnswers.map(answer =>
            answer.index === index ? { ...answer, text: value } : answer
        );
        setQuestionAnswers(newAnswers);
    }

    function handleRemoveAnswer(index) {
        if (questionAnswers.length > 1) {
            const newAnswers = questionAnswers
                .filter(answer => answer.index !== index)
                .map((answer, i) => ({ ...answer, index: i + 1 }));
            setQuestionAnswers(newAnswers);
        }
    }

    function handleDeleteQuestion(qIndex) {
        setQuestion(questions.filter((_, index) => qIndex !== index));
    }

    function handleEditQuestion(qIndex){
        setQuestionName(questions[qIndex].question);
        setQuestionAnswers(questions[qIndex].answers);
        handleDeleteQuestion(qIndex);
    }

    return (
        <>
            <section className={generalError ? 'errorInput' : ''}>
                <div className="quesAnsHeader">
                    <p className="sectionTitle">{t('quesAns')}</p>
                    <OutlinedPinkButton onPressFunction={() => setShowForm(!showForm)}>
                        {window.innerWidth <= 425
                            ? !showForm ? "+" : "–"
                            : !showForm ? t("addNew") : t("dismiss")
                        }
                    </OutlinedPinkButton>
                </div>

                {showForm &&
                    <div className="quesAnsContent">
                        <div className="inputContainer">
                            <FormField
                                label={t('question')}
                                placeholder={t('question')}
                                value={questionName}
                                onChange={(value) => setQuestionName(value)}
                                secondaryTheme
                                error={questionNameError}
                            />
                        </div>

                        <hr />

                        {questionAnswers.map((answer, index) => (
                            <div className="inputContainer" key={index}>
                                <FormField
                                    placeholder={`${t('answer')} ${answer.index}`}
                                    value={answer.text}
                                    onChange={(value) => handleAnswerChange(answer.index, value)}
                                    error={questionAnswersError}
                                    secondaryTheme
                                />
                                {questionAnswers.length > 2 && (
                                    <img
                                        src={BinImage}
                                        alt={t('remove')}
                                        onClick={() => handleRemoveAnswer(answer.index)}
                                        style={{ cursor: 'pointer' }}
                                    />
                                )}
                            </div>
                        ))}

                        <div className="doubleLineButton buttonAddAnswer">
                            <button onClick={handleAddAnswer}>
                                {t('addAnswer')}
                            </button>
                        </div>

                        <OutlinedPinkButton onPressFunction={handleNewQuestion} >
                            {t('addNewQuestion')}
                        </OutlinedPinkButton>
                    </div>
                }

                {questions.length > 0 &&
                    <div className='questionsContainer'>
                        {Array.isArray(questions) &&
                            questions.map((question, qIndex) => (
                                <div key={qIndex} className='questionContainer'>
                                    <div>
                                        <h1>Question {qIndex + 1}</h1>
                                        <h1>{question.question}</h1>
                                        <hr />
                                        {Array.isArray(question.answers) &&
                                            question.answers.map((answer, aIndex) => (
                                                <p key={aIndex}>- {answer.text}</p>
                                            ))
                                        }
                                    </div>
                                    <GreenRedOutlinedButtons
                                        greenName={t('edit')}
                                        redName={t('remove')}
                                        greenOnPress={() => handleEditQuestion(qIndex)}
                                        redOnPress={() => handleDeleteQuestion(qIndex)}
                                    />
                                </div>
                            ))
                        }
                    </div>
                }
            </section>
            {generalError && <p className='errorMessage'>{generalError}</p>}
        </>
    );
}

QuesAnswerForm.propTypes = {
    questions: PropTypes.arrayOf(
        PropTypes.shape({
            question: PropTypes.string.isRequired,
            answers: PropTypes.arrayOf(
                PropTypes.shape({
                    index: PropTypes.number.isRequired,
                    text: PropTypes.string.isRequired
                })
            ).isRequired
        })
    ).isRequired,
    setQuestion: PropTypes.func.isRequired,
    generalError: PropTypes.string
};

export default QuesAnswerForm;
